"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core"
import {
  Clear as ClearIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
} from "@material-ui/icons"
import firebase from "../firebase"
import Navbar from "./Navbar"

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value)
  const needsQuotes = /[",\n\r]/.test(s)
  const escaped = s.replace(/"/g, '""')
  return needsQuotes ? `"${escaped}"` : escaped
}

function toDateInputValue(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function startOfDay(dateStr) {
  const date = new Date(`${dateStr}T00:00:00`)
  return date
}

function endOfDay(dateStr) {
  const date = new Date(`${dateStr}T23:59:59.999`)
  return date
}

function formatTimestamp(ts) {
  if (!ts) return "-"
  const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString()
}

function defaultDateRange() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 6)
  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  }
}

function ScanEvents() {
  const defaults = useMemo(() => defaultDateRange(), [])
  const [fromDate, setFromDate] = useState(defaults.from)
  const [toDate, setToDate] = useState(defaults.to)
  const [searchTerm, setSearchTerm] = useState("")
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  const fetchEvents = useCallback(async (from, to) => {
    if (!from || !to) {
      setError("Seleccioná un rango de fechas válido")
      return
    }

    if (from > to) {
      setError("La fecha desde no puede ser posterior a la fecha hasta")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const snapshot = await firebase
        .firestore()
        .collection("scanEvents")
        .where("createdAt", ">=", startOfDay(from))
        .where("createdAt", "<=", endOfDay(to))
        .orderBy("createdAt", "desc")
        .get()

      const rows = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          qrCodeId: data.qrCodeId || "",
          gtin: data.gtin || "",
          delta: typeof data.delta === "number" ? data.delta : 1,
          createdAt: data.createdAt || null,
        }
      })

      setEvents(rows)
      setPage(0)
    } catch (err) {
      console.error("Error fetching scan events:", err)
      setError(err.message || "Error al cargar eventos de scan")
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEvents(defaults.from, defaults.to)
  }, [defaults.from, defaults.to, fetchEvents])

  const filteredEvents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return events

    return events.filter((event) => {
      const gtinMatch = event.gtin.toLowerCase().includes(term)
      const idMatch = event.qrCodeId.toLowerCase().includes(term)
      return gtinMatch || idMatch
    })
  }, [events, searchTerm])

  const totalDelta = useMemo(
    () => filteredEvents.reduce((sum, event) => sum + (event.delta || 0), 0),
    [filteredEvents],
  )

  const paginatedEvents = useMemo(
    () => filteredEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredEvents, page, rowsPerPage],
  )

  const handleApplyFilter = () => {
    fetchEvents(fromDate, toDate)
  }

  const clearSearch = () => {
    setSearchTerm("")
    setPage(0)
  }

  const exportToCsv = () => {
    const headers = ["qrCodeId", "gtin", "createdAt", "delta"]
    const lines = [headers.join(",")]

    for (const event of filteredEvents) {
      const createdAt =
        event.createdAt && typeof event.createdAt.toDate === "function"
          ? event.createdAt.toDate().toISOString()
          : ""
      lines.push(
        [event.qrCodeId, event.gtin, createdAt, event.delta].map(csvEscape).join(","),
      )
    }

    const csv = `\uFEFF${lines.join("\n")}\n`
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `scan-events-${fromDate}_to_${toDate}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" style={{ paddingTop: "2rem", paddingBottom: "2rem" }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" style={{ gap: "1rem" }}>
          <Box>
            <Typography variant="h4" style={{ fontWeight: 700 }}>
              Scan events
            </Typography>
            <Typography variant="body2" style={{ color: "#6b7280", marginTop: "0.25rem" }}>
              {filteredEvents.length} events • {totalDelta} redirects in range
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={exportToCsv}
            disabled={filteredEvents.length === 0 || loading}
          >
            Export CSV
          </Button>
        </Box>

        <Paper style={{ padding: "1rem", marginTop: "1.25rem" }}>
          <Box display="flex" alignItems="flex-end" flexWrap="wrap" style={{ gap: "1rem" }}>
            <TextField
              label="From"
              type="date"
              variant="outlined"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              variant="outlined"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" color="primary" onClick={handleApplyFilter} disabled={loading}>
              Apply filter
            </Button>
          </Box>

          <Box style={{ marginTop: "1rem" }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPage(0)
              }}
              placeholder="Filter by GTIN or QR code ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#6366f1" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>

        {error && (
          <Paper style={{ padding: "1rem", marginTop: "1rem", border: "1px solid #fecaca", background: "#fff1f2" }}>
            <Typography variant="body2" style={{ color: "#991b1b" }}>
              {error}
            </Typography>
            {(error.includes("permission") || error.includes("Permission") || error.includes("insufficient")) && (
              <Typography variant="body2" style={{ color: "#991b1b", marginTop: "0.5rem" }}>
                Las reglas de Firestore no permiten leer <code>scanEvents</code>. Publicá{" "}
                <code>firestore.rules</code> en Firebase Console → Firestore → Rules.
              </Typography>
            )}
          </Paper>
        )}

        <TableContainer component={Paper} className="qr-table" style={{ marginTop: "1rem" }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" style={{ padding: "3rem" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>QR Code ID</TableCell>
                    <TableCell>GTIN</TableCell>
                    <TableCell align="right">Delta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEvents.length > 0 ? (
                    paginatedEvents.map((event) => (
                      <TableRow key={event.id} hover>
                        <TableCell>{formatTimestamp(event.createdAt)}</TableCell>
                        <TableCell style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                          {event.qrCodeId || "-"}
                        </TableCell>
                        <TableCell style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                          {event.gtin || "-"}
                        </TableCell>
                        <TableCell align="right">{event.delta}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body1" style={{ padding: "2rem 0" }}>
                          No scan events in this range
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={filteredEvents.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[25, 50, 100, 250]}
              />
            </>
          )}
        </TableContainer>
      </Container>
    </>
  )
}

export default ScanEvents
